import React, { useContext, useEffect, useState } from "react";

import { MoreVert } from "@mui/icons-material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  Alert,
  Box,
  Chip,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Snackbar,
  Switch,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { Clipboard } from "react-feather";
import { useParams } from "react-router-dom";

import { Money } from "@safetyculture/s12-apis-connect-web/s12/common/money_pb";
import {
  Contract,
  ContractStatus,
} from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/contracts_pb";
import { ActionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/action_pb";
import { SyncContractAction } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_external_pb";
import { Environment } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/workflows_pb";

import { contractStatusToString } from "../../../../constants/contracts";
import {
  convertTimestampToDate,
  formatDateTimeDisplay,
} from "../../../../helpers/time";
import mainStyles from "../../../../styles";
import ContractPlanRecord from "./org-contract-plan-record";
import { getSubscriptionComponentBySubId } from "api/endpoints/components";
import {
  getOrgContractList,
  setContractEnterpriseState,
  setContractMSAState,
} from "api/endpoints/contracts";
import { syncContractData } from "api/endpoints/external-syncs";
import { callOptionsHeaders } from "api/helpers";
import { DEFAULT_SNACKBAR_DURATION } from "constants/index";
import {
  ContractsListContext,
  OrgDataContext,
} from "context/organisation-context";
import { PermissionsContext } from "context/permissions-context";
import { SnackbarContext } from "context/snackbar-context";
import { getEnvironment } from "helpers/environment";
import { toUUID } from "helpers/s12-helpers";
import { UrlParamTypes } from "types/org-display";

interface ContractRecordProps {
  contract: Contract;
  contractsTabColSpan: number;
}

// Convert an s12.common.Money value into a number of cents (may be fractional
// for sub-cent prices) so it can flow through the existing priceInCents-based
// display pipeline.
const moneyToCents = (money?: Money): number | undefined =>
  money ? Number(money.units) * 100 + money.nanos / 1e7 : undefined;

const ContractRecord = ({
  contract,
  contractsTabColSpan,
}: ContractRecordProps) => {
  const theme = useTheme();
  const classes = mainStyles();
  const { roleId, regionCode } = useParams<UrlParamTypes>();
  const { showError, copyToClipboard } = useContext(SnackbarContext);
  const { hasPermissions, permissions } = useContext(PermissionsContext);
  const { orgData } = useContext(OrgDataContext);
  const [disableAllSwitches, setDisableAllSwitches] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const { setContractsList } = useContext(ContractsListContext);
  const subscription = orgData?.subscription?.[0];
  const isZuoraOrg = subscription?.billingProvider === "ZUORA";
  // Zuora holds the authoritative enterprise state, so the toggle stays locked in
  // production. Dev and sandpit allow overriding it so internal enterprise-tier
  // behaviour can be exercised against Zuora-billed orgs.
  const env = getEnvironment();
  const isDevOrSandpit =
    env === Environment.DEVELOPMENT || env === Environment.SANDPIT;
  const enterpriseLockedByZuora = isZuoraOrg && !isDevOrSandpit;
  // For Zuora orgs the contract's priceInCents comes back as 0 (Zuora holds the
  // authoritative price), so fetch the subscription's real component prices and
  // build a lookup keyed by price-point id (and componentId as a fallback) that
  // the components table uses to fill in prices. See plan for details.
  const [priceOverrides, setPriceOverrides] = useState<Map<number, number>>(
    new Map(),
  );

  useEffect(() => {
    const subId = subscription?.id;
    if (!isZuoraOrg || !subId) {
      return;
    }
    getSubscriptionComponentBySubId(
      { subId: toUUID(subId) },
      callOptionsHeaders(regionCode, roleId),
    )
      .then((res) => {
        const overrides = new Map<number, number>();
        res.subscriptionComponents.forEach((component) => {
          component.componentPrice.forEach((cp) => {
            // Use the non-deprecated Money field (component_price); the legacy
            // `price` cents field is deprecated. Fall back to it only if Money
            // isn't populated so older data still renders.
            const priceCents = moneyToCents(cp.componentPrice) ?? cp.price;
            overrides.set(cp.id, priceCents);
            // Fallback key: only set if a price-point id hasn't already claimed it.
            if (!overrides.has(cp.componentId)) {
              overrides.set(cp.componentId, priceCents);
            }
          });
        });
        setPriceOverrides(overrides);
      })
      .catch(showError);
  }, [isZuoraOrg, subscription?.id, regionCode, showError]);

  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  const handleActionMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setActionMenuAnchor(event.currentTarget);
  };

  const contractStatusChipStyleMap = {
    [ContractStatus.UNSPECIFIED]: classes.neutral,
    [ContractStatus.ACTIVATED]: classes.greenDay,
    [ContractStatus.CANCELED]: classes.redWine,
    [ContractStatus.EXPIRED]: classes.purpleRain,
    [ContractStatus.PAST_DUE]: classes.yellowSubmarine,
    [ContractStatus.TRIALING]: classes.blueMoon,
    [ContractStatus.TRIAL_ENDED]: classes.blueMoon,
  };

  const canSyncContract =
    !permissions.isPermissionsLoading &&
    hasPermissions([ActionType.SYNC_CONTRACT_DATA]);
  const canSetEnterprise =
    !permissions.isPermissionsLoading &&
    hasPermissions([ActionType.SET_CONTRACT_ENTERPRISE_STATE]);
  const canSetMSA =
    !permissions.isPermissionsLoading &&
    hasPermissions([ActionType.SET_CONTRACT_MSA_STATE]);

  const handleToggleEnterprise = (
    event: React.ChangeEvent<HTMLInputElement>,
    contract: Contract,
  ) => {
    setDisableAllSwitches(true);
    const isEnterprise = event.target.checked;
    setContractEnterpriseState(
      {
        contractId: contract.id,
        hasEnterprise: isEnterprise,
      },
      callOptionsHeaders(regionCode, roleId),
    )
      .then(() => {
        getOrgContractList(
          { orgId: roleId },
          callOptionsHeaders(regionCode, roleId),
        )
          .then((res) => {
            setContractsList(res);
          })
          .catch(showError);
      })
      .catch(showError)
      .finally(() => {
        setDisableAllSwitches(false);
      });
  };

  const handleToggleMSA = (
    event: React.ChangeEvent<HTMLInputElement>,
    contract: Contract,
  ) => {
    setDisableAllSwitches(true);
    const isMSA = event.target.checked;
    setContractMSAState(
      {
        contractId: contract.id,
        hasMasterServiceAgreement: isMSA,
      },
      callOptionsHeaders(regionCode, roleId),
    )
      .then(() => {
        getOrgContractList(
          { orgId: roleId },
          callOptionsHeaders(regionCode, roleId),
        )
          .then((res) => {
            setContractsList(res);
          })
          .catch(showError);
      })
      .catch((error) => {
        showError(error);
      })
      .finally(() => {
        setDisableAllSwitches(false);
      });
  };

  const resyncAction = async (action: number) => {
    setDisableAllSwitches(true);
    syncContractData(
      {
        organisationId: orgData.id,
        subscriptionId: subscription.id,
        action: action,
      },
      callOptionsHeaders(regionCode, roleId),
    )
      .then(() => {
        setOpenSnackbar(true);
      })
      .catch((error) => {
        showError(error);
      })
      .finally(() => {
        setDisableAllSwitches(false);
      });
  };

  const displayContractMenu = () => {
    const menuItems: Array<{ item: string; component: React.ReactElement }> = [
      {
        item: "sync-contract",
        component: (
          <>
            <MenuItem
              onClick={() => {
                resyncAction(SyncContractAction.CONTRACT_CREATE_ACTION);
                setActionMenuAnchor(null);
              }}
              data-testid="contracts-create-contract-menu-item"
            >
              Sync - Create
            </MenuItem>
            <MenuItem
              onClick={() => {
                resyncAction(SyncContractAction.CONTRACT_RENEWAL_ACTION);
                setActionMenuAnchor(null);
              }}
              data-testid="contracts-renew-contract-menu-item"
            >
              Sync - Renew
            </MenuItem>
            <MenuItem
              onClick={() => {
                resyncAction(SyncContractAction.CONTRACT_UPDATE_ACTION);
                setActionMenuAnchor(null);
              }}
              data-testid="contracts-update-contract-menu-item"
            >
              Sync - Update
            </MenuItem>
            <MenuItem
              onClick={() => {
                resyncAction(SyncContractAction.CONTRACT_SEAT_CHANGE_ACTION);
                setActionMenuAnchor(null);
              }}
              data-testid="contracts-seat-change-contract-menu-item"
            >
              Sync - Seats
            </MenuItem>
          </>
        ),
      },
    ];

    const menuList: React.ReactElement[] = [];
    menuItems.forEach((menuItem) => {
      const { item, component } = menuItem;

      switch (item) {
        case "sync-contract":
          if (canSyncContract && contract.status === ContractStatus.ACTIVATED) {
            return menuList.push(component);
          }
          return;
        default:
          return;
      }
    });

    return (
      <>
        <Box>
          <IconButton
            onClick={handleActionMenuClick}
            data-testid={"contracts-actions-menu-button"}
          >
            <MoreVert />
          </IconButton>
          <Menu
            open={Boolean(actionMenuAnchor)}
            anchorEl={actionMenuAnchor}
            onClose={() => setActionMenuAnchor(null)}
            defaultValue={""}
          >
            {menuList.length > 0 ? (
              menuList
            ) : (
              <MenuItem
                onClick={() => {
                  setActionMenuAnchor(null);
                }}
                disabled={true}
                data-testid="contracts-no-actions-available"
              >
                No actions available
              </MenuItem>
            )}
          </Menu>
        </Box>
      </>
    );
  };

  return (
    <>
      <TableRow key={`contracts-row-${contract.id}`}>
        <TableCell
          padding="checkbox"
          data-testid={`contracts-plan-expand-row-${contract.id}`}
        >
          <IconButton
            aria-label={"contracts-expand-row-button"}
            data-testid={"contracts-expand-row-button"}
            size="small"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>

        <TableCell
          align="left"
          data-testid={"contracts-id"}
          style={{ verticalAlign: "center" }}
        >
          <Typography
            variant="body2"
            align="left"
            sx={classes.clickable}
            onClick={() => copyToClipboard(contract.id)}
          >
            {contract.id}{" "}
            <Clipboard
              color={theme.palette.text.primary}
              size={"1rem"}
              className="position-icon"
            />
          </Typography>
        </TableCell>

        <TableCell align="left" data-testid={"contracts-status"}>
          <Chip
            size="small"
            style={{ fontWeight: "bold" }}
            sx={contractStatusChipStyleMap[contract.status]}
            label={contractStatusToString[contract.status]}
          />
        </TableCell>

        <TableCell align="left" data-testid={"contracts-term-in-months"}>
          {contract.contractTermInMonths}
        </TableCell>

        <TableCell align="left" data-testid={"contracts-start-date"}>
          {contract.startDate &&
            formatDateTimeDisplay(convertTimestampToDate(contract.startDate))}
        </TableCell>

        <TableCell align="left" data-testid={"contracts-end-date"}>
          {contract.endDate &&
            formatDateTimeDisplay(convertTimestampToDate(contract.endDate))}
        </TableCell>

        <TableCell align="left" data-testid={"contracts-has-enterprise"}>
          {canSetEnterprise && contract.status === ContractStatus.ACTIVATED ? (
            <Tooltip
              title={
                enterpriseLockedByZuora
                  ? "Enterprise state is managed by Zuora and cannot be changed here"
                  : ""
              }
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={contract.hasEnterprise}
                    onChange={(event) =>
                      handleToggleEnterprise(event, contract)
                    }
                    disabled={disableAllSwitches || enterpriseLockedByZuora}
                    color="primary"
                    data-testid="is-enterprise-switch"
                  />
                }
                label=""
              />
            </Tooltip>
          ) : (
            contract.hasEnterprise.toString()
          )}
        </TableCell>

        <TableCell align="left" data-testid="contracts-has-msa">
          {canSetMSA && contract.status === ContractStatus.ACTIVATED ? (
            <Tooltip
              title={
                isZuoraOrg
                  ? "MSA agreement is managed by Zuora and cannot be changed here"
                  : ""
              }
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={contract.hasMasterServiceAgreement}
                    onChange={(event) => handleToggleMSA(event, contract)}
                    disabled={disableAllSwitches || isZuoraOrg}
                    color="primary"
                    data-testid="is-msa-switch"
                  />
                }
                label=""
              />
            </Tooltip>
          ) : (
            contract.hasMasterServiceAgreement.toString()
          )}
        </TableCell>

        <TableCell align="left" data-testid="contracts-metadata">
          {contract.metadata ? (
            <Tooltip
              title={
                <pre
                  style={{
                    whiteSpace: "pre-wrap",
                    margin: 0,
                    fontFamily: "monospace",
                  }}
                >
                  {JSON.stringify(
                    JSON.parse(contract.metadata.toJsonString()),
                    null,
                    2,
                  )}
                </pre>
              }
            >
              <Typography variant="body2" color="primary">
                View
              </Typography>
            </Tooltip>
          ) : (
            "-"
          )}
        </TableCell>

        <TableCell align="left" data-testid="contracts-actions-menu">
          {!isZuoraOrg && displayContractMenu()}
        </TableCell>
      </TableRow>
      <TableRow>
        <ContractPlanRecord
          contractPlans={contract.plans}
          contractsTabColSpan={contractsTabColSpan}
          isExpanded={isExpanded}
          priceOverrides={priceOverrides}
        />
      </TableRow>

      <Snackbar
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        open={openSnackbar}
        autoHideDuration={DEFAULT_SNACKBAR_DURATION}
        onClose={() => setOpenSnackbar(false)}
        data-testid="snackbar-contracts-sync"
      >
        <Alert severity="success" variant="filled">
          Contract sync has been triggered - please expect that the sync will
          take some time to complete
        </Alert>
      </Snackbar>
    </>
  );
};

export default ContractRecord;
